import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { createRoute, useNavigate, type AnyRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { BookPlus, Library } from 'lucide-react';
import { ErrorNotice, Spinner } from '@nw/ui';
import { ModuleGate, api, type WebModule } from '@nw/web-platform';
import { BOOK_STATUSES, type BookNote } from '../public/index.js';
import type { LibrarySearch } from './LibraryPage.js';
import { libraryCommands, libraryWidgets, useLibraryStatus } from './assistant.js';

const LibraryPage = lazy(() => import('./LibraryPage.js').then((m) => ({ default: m.LibraryPage })));
const ReadingRoom = lazy(() => import('./ReadingRoom.js').then((m) => ({ default: m.ReadingRoom })));
const AddBookFlow = lazy(() => import('./forms.js').then((m) => ({ default: m.AddBookFlow })));

function Paper({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <div data-surface="paper" className="grid min-h-[60vh] place-items-center">
          <Spinner label="Opening the library" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}
const searchSchema = z.object({
  q: z.string().optional(),
  status: z.enum(BOOK_STATUSES).optional(),
  shelf: z.string().optional(),
  tag: z.string().optional(),
  author: z.string().optional(),
  view: z.enum(['shelf', 'list']).optional(),
});

function NoteRedirect({ noteId }: { noteId: string }) {
  const navigate = useNavigate();
  const note = useQuery({
    queryKey: ['library', 'note', noteId],
    queryFn: async () => (await api<BookNote>(`/library/notes/${noteId}`)).data,
    retry: false,
  });
  useEffect(() => {
    if (note.data)
      void navigate({
        to: '/library/books/$bookId',
        params: { bookId: note.data.bookId },
        replace: true,
      });
  }, [note.data, navigate]);
  if (note.isError) return <ErrorNotice className="m-10" title="This note is not available" detail="It may have been deleted along with its book." />;
  return <Spinner className="m-10" />;
}

export const libraryWeb: WebModule = {
  id: 'library',
  nav: {
    label: 'Library',
    path: '/library',
    order: 10,
    icon: Library,
    tone: 'sage',
    category: 'Reading',
    description: 'Books, shelves, reading and notes',
  },
  windows: (pathname) => {
    const read = pathname.match(/^\/library\/books\/([^/]+)\/read$/);
    if (read)
      return {
        key: `library:read:${read[1]}`,
        title: 'Reading room',
        moduleId: 'library',
        tone: 'sage',
      };
    if (pathname === '/library' || pathname.startsWith('/library/'))
      return {
        key: 'library',
        title: 'Library',
        moduleId: 'library',
        tone: 'sage',
      };
    return null;
  },
  space: { hue: 95, mode: 'light' },
  useStatus: useLibraryStatus,
  commands: libraryCommands,
  widgets: libraryWidgets,
  routes: (parent: AnyRoute) => {
    const list = createRoute({
      getParentRoute: () => parent,
      path: '/library',
      validateSearch: searchSchema,
      component: function LibraryRoute() {
        const search = list.useSearch() as LibrarySearch;
        return (
          <ModuleGate moduleId="library" name="Library">
            <Paper>
              <LibraryPage search={search} selectedId={null} />
            </Paper>
          </ModuleGate>
        );
      },
    });
    const book = createRoute({
      getParentRoute: () => parent,
      path: '/library/books/$bookId',
      validateSearch: searchSchema,
      component: function BookRoute() {
        const search = book.useSearch() as LibrarySearch;
        const { bookId } = book.useParams() as { bookId: string };
        return (
          <ModuleGate moduleId="library" name="Library">
            <Paper>
              <LibraryPage search={search} selectedId={bookId} />
            </Paper>
          </ModuleGate>
        );
      },
    });
    const read = createRoute({
      getParentRoute: () => parent,
      path: '/library/books/$bookId/read',
      component: function ReadRoute() {
        const { bookId } = read.useParams() as { bookId: string };
        return (
          <ModuleGate moduleId="library" name="Library">
            <Paper>
              <ReadingRoom bookId={bookId} />
            </Paper>
          </ModuleGate>
        );
      },
    });
    const note = createRoute({
      getParentRoute: () => parent,
      path: '/library/notes/$noteId',
      component: function NoteRoute() {
        const { noteId } = note.useParams() as { noteId: string };
        return (
          <ModuleGate moduleId="library" name="Library">
            <NoteRedirect noteId={noteId} />
          </ModuleGate>
        );
      },
    });
    return [list, book, read, note];
  },
  quickActions: [
    {
      id: 'library.add-book',
      label: 'Add a book',
      description: 'Type it in or look it up in Open Library.',
      icon: BookPlus,
      Component: ({ onDone }) => (
        <div data-surface="paper">
          <Suspense fallback={<Spinner />}>
            <AddBookFlow onDone={onDone} />
          </Suspense>
        </div>
      ),
    },
  ],
};
